<script setup>
/* global axios */
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  knowledgeBase: { type: Object, required: true },
});

const store = useStore();
const { t } = useI18n();

const isLoading = ref(false);
const isUploading = ref(false);
const isDragging = ref(false);
const fileInput = ref(null);
const expandedSourceId = ref(null);
const isLoadingChunks = ref(false);
const editingChunkId = ref(null);
const editChunkContent = ref('');
const isSavingChunk = ref(false);
const pollTimer = ref(null);
// { [chunkId]: 'processing' | 'ready' | 'error' }
const chunkStatusMap = ref({});

const accountId = computed(() => store.getters.getCurrentAccountId);
const chunks = computed(() =>
  expandedSourceId.value
    ? store.getters['knowledgeBases/getChunksBySource'](expandedSourceId.value)
    : []
);
const sources = computed(() =>
  store.getters['knowledgeBases/getSourcesByKnowledgeBase'](
    props.knowledgeBase.id
  ).filter(s => s.source_type === 'file')
);

const ACCEPTED_TYPES = '.pdf,.md,.docx,.txt';
const MAX_SIZE_MB = 20;
const CHUNK_EMBEDDING_TIMEOUT_MS = 5000;
const CHUNK_READY_DISPLAY_MS = 2000;
const POLL_INTERVAL_MS = 2000;

const statusIcon = status =>
  ({
    pending: 'i-lucide-clock',
    processing: 'i-lucide-loader-circle',
    ready: 'i-lucide-check-circle',
    error: 'i-lucide-x-circle',
  })[status] || 'i-lucide-clock';

const statusClass = status =>
  ({
    pending: 'text-n-slate-11',
    processing: 'text-yellow-600 animate-spin',
    ready: 'text-green-600',
    error: 'text-red-600',
  })[status] || 'text-n-slate-11';

const fileTypeIcon = type =>
  ({
    pdf: 'i-lucide-file-text',
    md: 'i-lucide-file-code',
    markdown: 'i-lucide-file-code',
    docx: 'i-lucide-file-type',
    txt: 'i-lucide-file',
  })[type] || 'i-lucide-file';

// --- Polling de status das sources ---

const startPolling = () => {
  if (pollTimer.value) return;
  pollTimer.value = setInterval(async () => {
    await store.dispatch('knowledgeBases/getSources', {
      accountId: accountId.value,
      knowledgeBaseId: props.knowledgeBase.id,
    });
    const hasProcessing = sources.value.some(
      s => s.status === 'pending' || s.status === 'processing'
    );
    if (!hasProcessing) {
      clearInterval(pollTimer.value);
      pollTimer.value = null;
    }
  }, POLL_INTERVAL_MS);
};

const stopPolling = () => {
  if (pollTimer.value) {
    clearInterval(pollTimer.value);
    pollTimer.value = null;
  }
};

// --- Upload ---

const uploadFile = async file => {
  const ext = file.name.split('.').pop().toLowerCase();
  const allowed = ['pdf', 'md', 'docx', 'txt', 'markdown'];
  if (!allowed.includes(ext)) {
    useAlert(t('KNOWLEDGE_BASE.FILES.ERROR.UNSUPPORTED_TYPE'));
    return;
  }
  if (file.size > MAX_SIZE_MB * 1024 * 1024) {
    useAlert(t('KNOWLEDGE_BASE.FILES.ERROR.TOO_LARGE', { max: MAX_SIZE_MB }));
    return;
  }

  isUploading.value = true;
  try {
    const formData = new FormData();
    formData.append('knowledge_source[name]', file.name);
    formData.append('knowledge_source[source_type]', 'file');
    formData.append('knowledge_source[file_type]', ext);
    formData.append('knowledge_source[file]', file);

    const kbId = props.knowledgeBase.id;
    const acctId = accountId.value;
    const response = await axios.post(
      `/api/v1/accounts/${acctId}/knowledge_bases/${kbId}/knowledge_sources`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    const source = response.data.data;
    store.commit('knowledgeBases/ADD_SOURCE', {
      knowledgeBaseId: kbId,
      source: {
        ...source,
        status: source.status === 'pending' ? 'processing' : source.status,
      },
    });
    useAlert(t('KNOWLEDGE_BASE.FILES.UPLOAD.SUCCESS'));
    startPolling();
  } catch {
    useAlert(t('KNOWLEDGE_BASE.FILES.UPLOAD.ERROR'));
  } finally {
    isUploading.value = false;
  }
};

// --- Drag-drop ---

const onDragOver = () => {
  isDragging.value = true;
};
const onDragLeave = () => {
  isDragging.value = false;
};

const onDrop = e => {
  isDragging.value = false;
  const files = Array.from(e.dataTransfer?.files || []);
  if (files.length) uploadFile(files[0]);
};

const onFileSelect = e => {
  const file = e.target.files?.[0];
  if (file) uploadFile(file);
  fileInput.value.value = '';
};

// --- Chunks ---

const toggleChunks = async source => {
  if (expandedSourceId.value === source.id) {
    expandedSourceId.value = null;
    editingChunkId.value = null;
    return;
  }
  expandedSourceId.value = source.id;
  editingChunkId.value = null;
  isLoadingChunks.value = true;
  try {
    await store.dispatch('knowledgeBases/getChunks', {
      accountId: accountId.value,
      knowledgeBaseId: props.knowledgeBase.id,
      sourceId: source.id,
    });
  } finally {
    isLoadingChunks.value = false;
  }
};

const startEditChunk = chunk => {
  editingChunkId.value = chunk.id;
  editChunkContent.value = chunk.content;
};

const cancelEditChunk = () => {
  editingChunkId.value = null;
  editChunkContent.value = '';
};

const saveChunk = async chunk => {
  if (!editChunkContent.value.trim()) return;
  isSavingChunk.value = true;
  const sourceId = expandedSourceId.value;
  try {
    await store.dispatch('knowledgeBases/updateChunk', {
      accountId: accountId.value,
      knowledgeBaseId: props.knowledgeBase.id,
      sourceId,
      chunkId: chunk.id,
      content: editChunkContent.value.trim(),
    });
    cancelEditChunk();
    chunkStatusMap.value = {
      ...chunkStatusMap.value,
      [chunk.id]: 'processing',
    };
    setTimeout(() => {
      chunkStatusMap.value = { ...chunkStatusMap.value, [chunk.id]: 'ready' };
      setTimeout(() => {
        const updated = { ...chunkStatusMap.value };
        delete updated[chunk.id];
        chunkStatusMap.value = updated;
      }, CHUNK_READY_DISPLAY_MS);
    }, CHUNK_EMBEDDING_TIMEOUT_MS);
  } catch {
    useAlert(t('KNOWLEDGE_BASE.CHUNKS.EDIT.ERROR'));
    chunkStatusMap.value = { ...chunkStatusMap.value, [chunk.id]: 'error' };
  } finally {
    isSavingChunk.value = false;
  }
};

const deleteChunk = async chunk => {
  try {
    await store.dispatch('knowledgeBases/deleteChunk', {
      accountId: accountId.value,
      knowledgeBaseId: props.knowledgeBase.id,
      sourceId: expandedSourceId.value,
      chunkId: chunk.id,
    });
    useAlert(t('KNOWLEDGE_BASE.CHUNKS.DELETE.SUCCESS'));
  } catch {
    useAlert(t('KNOWLEDGE_BASE.CHUNKS.DELETE.ERROR'));
  }
};

// --- Sources actions ---

const reprocess = async source => {
  store.commit('knowledgeBases/UPDATE_SOURCE', {
    knowledgeBaseId: props.knowledgeBase.id,
    source: { ...source, status: 'processing' },
  });
  try {
    await store.dispatch('knowledgeBases/reprocessSource', {
      accountId: accountId.value,
      knowledgeBaseId: props.knowledgeBase.id,
      sourceId: source.id,
    });
    useAlert(t('KNOWLEDGE_BASE.FILES.REPROCESS.SUCCESS'));
    startPolling();
  } catch {
    useAlert(t('KNOWLEDGE_BASE.FILES.REPROCESS.ERROR'));
  }
};

const deleteSource = async source => {
  try {
    await store.dispatch('knowledgeBases/deleteSource', {
      accountId: accountId.value,
      knowledgeBaseId: props.knowledgeBase.id,
      sourceId: source.id,
    });
    useAlert(t('KNOWLEDGE_BASE.FILES.DELETE.SUCCESS'));
  } catch {
    useAlert(t('KNOWLEDGE_BASE.FILES.DELETE.ERROR'));
  }
};

onMounted(async () => {
  isLoading.value = true;
  try {
    await store.dispatch('knowledgeBases/getSources', {
      accountId: accountId.value,
      knowledgeBaseId: props.knowledgeBase.id,
    });
    if (
      sources.value.some(
        s => s.status === 'pending' || s.status === 'processing'
      )
    ) {
      startPolling();
    }
  } finally {
    isLoading.value = false;
  }
});

onUnmounted(() => {
  stopPolling();
});
</script>

<template>
  <div class="space-y-4">
    <div>
      <h3 class="text-base font-semibold text-n-slate-12 m-0">
        {{ $t('KNOWLEDGE_BASE.FILES.TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11 mt-0.5 m-0">
        {{ $t('KNOWLEDGE_BASE.FILES.DESCRIPTION') }}
      </p>
    </div>

    <!-- Drop zone -->
    <div
      class="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors"
      :class="
        isDragging
          ? 'border-woot-500 bg-woot-50'
          : 'border-n-weak hover:border-n-medium'
      "
      @click="fileInput?.click()"
      @dragover.prevent="onDragOver"
      @dragleave="onDragLeave"
      @drop.prevent="onDrop"
    >
      <input
        ref="fileInput"
        type="file"
        :accept="ACCEPTED_TYPES"
        class="hidden"
        @change="onFileSelect"
      />
      <div v-if="!isUploading" class="flex flex-col items-center gap-2">
        <span class="i-lucide-upload-cloud w-10 h-10 text-n-slate-11" />
        <p class="text-sm font-medium text-n-slate-12 m-0">
          {{ $t('KNOWLEDGE_BASE.FILES.DROP_ZONE.TITLE') }}
        </p>
        <p class="text-xs text-n-slate-11 m-0">
          {{
            $t('KNOWLEDGE_BASE.FILES.DROP_ZONE.SUBTITLE', { max: MAX_SIZE_MB })
          }}
        </p>
        <p class="text-xs text-n-slate-10 m-0">
          {{ $t('KNOWLEDGE_BASE.FILES.DROP_ZONE.FORMATS') }}
        </p>
      </div>
      <div v-else class="flex flex-col items-center gap-2">
        <Spinner />
        <p class="text-sm text-n-slate-11 m-0">
          {{ $t('KNOWLEDGE_BASE.FILES.UPLOADING') }}
        </p>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="flex justify-center py-10">
      <Spinner />
    </div>

    <!-- Lista de arquivos -->
    <div v-else-if="sources.length" class="divide-y divide-n-weak">
      <div v-for="source in sources" :key="source.id">
        <!-- Linha do arquivo -->
        <div class="py-3 flex items-center gap-3">
          <span
            class="w-8 h-8 shrink-0 flex items-center justify-center rounded-lg bg-n-alpha-2"
          >
            <span
              :class="`i-lucide w-4 h-4 text-n-slate-11 ${fileTypeIcon(source.file_type)}`"
            />
          </span>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-n-slate-12 truncate m-0">
              {{ source.name }}
            </p>
            <div class="flex items-center gap-1.5 mt-0.5">
              <span
                :class="`i-lucide w-3 h-3 ${statusIcon(source.status)} ${statusClass(source.status)}`"
              />
              <span class="text-xs text-n-slate-11">
                {{
                  $t(
                    `KNOWLEDGE_BASE.SOURCE_STATUS.${source.status?.toUpperCase()}`
                  )
                }}
              </span>
              <span
                v-if="source.status === 'error'"
                class="text-xs text-red-600"
              >
                — {{ source.error_message }}
              </span>
            </div>
          </div>
          <div class="flex gap-1 shrink-0">
            <Button
              v-if="source.status === 'ready'"
              v-tooltip.top="$t('KNOWLEDGE_BASE.CHUNKS.VIEW_BUTTON')"
              :icon="
                expandedSourceId === source.id
                  ? 'i-lucide-chevron-up'
                  : 'i-lucide-layers'
              "
              xs
              slate
              faded
              @click="toggleChunks(source)"
            />
            <Button
              v-tooltip.top="$t('KNOWLEDGE_BASE.FILES.REPROCESS.BUTTON')"
              icon="i-lucide-refresh-cw"
              xs
              slate
              faded
              :disabled="
                source.status === 'processing' || source.status === 'pending'
              "
              @click="reprocess(source)"
            />
            <Button
              v-tooltip.top="$t('KNOWLEDGE_BASE.FILES.DELETE.BUTTON')"
              icon="i-lucide-trash-2"
              xs
              ruby
              faded
              @click="deleteSource(source)"
            />
          </div>
        </div>

        <!-- Painel de Chunks -->
        <div
          v-if="expandedSourceId === source.id"
          class="mb-3 border border-n-weak rounded-lg overflow-hidden"
        >
          <div
            class="flex items-center justify-between px-3 py-2 bg-n-alpha-1 border-b border-n-weak"
          >
            <span
              class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide"
            >
              {{ $t('KNOWLEDGE_BASE.CHUNKS.TITLE') }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ $t('KNOWLEDGE_BASE.CHUNKS.COUNT', { count: chunks.length }) }}
            </span>
          </div>

          <div v-if="isLoadingChunks" class="flex justify-center py-6">
            <Spinner />
          </div>

          <div
            v-else-if="chunks.length"
            class="divide-y divide-n-weak max-h-96 overflow-y-auto"
          >
            <div v-for="chunk in chunks" :key="chunk.id" class="p-3">
              <!-- Modo edição do chunk -->
              <div v-if="editingChunkId === chunk.id" class="space-y-2">
                <div class="flex items-center justify-between mb-1">
                  <span class="text-xs text-n-slate-10"
                    >#{{ chunk.chunk_number }}</span
                  >
                  <span class="text-xs text-n-slate-10">
                    {{
                      $t('KNOWLEDGE_BASE.CHUNKS.TOKEN_COUNT', {
                        count: chunk.token_count,
                      })
                    }}
                  </span>
                </div>
                <textarea
                  v-model="editChunkContent"
                  class="input text-xs"
                  rows="6"
                />
                <div class="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="xs"
                    :label="$t('KNOWLEDGE_BASE.FORM.CANCEL')"
                    @click="cancelEditChunk"
                  />
                  <Button
                    size="xs"
                    :label="$t('KNOWLEDGE_BASE.CHUNKS.EDIT.SAVE')"
                    :is-loading="isSavingChunk"
                    @click="saveChunk(chunk)"
                  />
                </div>
              </div>

              <!-- Modo visualização do chunk -->
              <div v-else class="flex items-start gap-2">
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2 mb-1">
                    <span class="text-xs font-medium text-n-slate-10"
                      >#{{ chunk.chunk_number }}</span
                    >
                    <span class="text-xs text-n-slate-10">
                      {{
                        $t('KNOWLEDGE_BASE.CHUNKS.TOKEN_COUNT', {
                          count: chunk.token_count,
                        })
                      }}
                    </span>
                    <span
                      v-if="chunk.chunking_strategy"
                      class="text-xs text-n-slate-9 italic"
                      >{{ chunk.chunking_strategy }}</span
                    >
                    <!-- Status do embedding após edição -->
                    <span
                      v-if="chunkStatusMap[chunk.id]"
                      v-tooltip.top="
                        $t(
                          `KNOWLEDGE_BASE.CHUNKS.EMBEDDING_STATUS.${chunkStatusMap[chunk.id].toUpperCase()}`
                        )
                      "
                      :class="`i-lucide w-3 h-3 ${statusIcon(chunkStatusMap[chunk.id])} ${statusClass(chunkStatusMap[chunk.id])}`"
                    />
                  </div>
                  <p class="text-xs text-n-slate-12 m-0 whitespace-pre-wrap">
                    {{ chunk.content }}
                  </p>
                </div>
                <div class="flex gap-1 shrink-0 mt-0.5">
                  <Button
                    v-tooltip.top="$t('KNOWLEDGE_BASE.CHUNKS.EDIT.BUTTON')"
                    icon="i-lucide-pencil"
                    xs
                    slate
                    faded
                    :disabled="!!chunkStatusMap[chunk.id]"
                    @click="startEditChunk(chunk)"
                  />
                  <Button
                    v-tooltip.top="$t('KNOWLEDGE_BASE.CHUNKS.DELETE.BUTTON')"
                    icon="i-lucide-trash-2"
                    xs
                    ruby
                    faded
                    @click="deleteChunk(chunk)"
                  />
                </div>
              </div>
            </div>
          </div>

          <div v-else class="text-center py-6">
            <p class="text-xs text-n-slate-11 m-0">
              {{ $t('KNOWLEDGE_BASE.CHUNKS.EMPTY') }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Vazio -->
    <div v-else-if="!isLoading" class="text-center py-6">
      <p class="text-sm text-n-slate-11">
        {{ $t('KNOWLEDGE_BASE.FILES.EMPTY') }}
      </p>
    </div>
  </div>
</template>
